/**
 * Auth — the same three ways in the Studio has (an email link, an email + password, or
 * Google), and the claim that gates this app.
 *
 * The difference from the Studio's provider is what the session is *for*. There is no
 * `authorId` here: the admin app never addresses `authors/{authorId}/…`, and giving it one
 * would suggest it could. What it carries instead is the `admin` claim, read off the ID token
 * with `onIdTokenChanged` (not `onAuthStateChanged`) so that granting the claim and
 * refreshing is enough — a claim minted while the tab is open arrives on the next token.
 * The claim is on the user, not on the method: however an admin comes in, the token says.
 *
 * The house rules are the Studio's: Google is a popup and **never a redirect**; an address
 * that already has a way in is **never linked silently**; and nothing is blocked on email
 * verification. Nothing is written to Firestore from here — the Studio's first-Google-sign-in
 * profile line is the Studio's, and this app has no profile to keep.
 *
 * **The three mails are not sent from here** either (`general/08`): the sign-in link, the
 * verification and the reset go through the `requestAuthMail` callable with `app: 'admin'`,
 * so the link comes back to this app. The tool is English, so the mail is too.
 */

import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  isSignInWithEmailLink,
  linkWithCredential,
  onIdTokenChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  updatePassword,
} from 'firebase/auth';
import type { User } from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';

import { firebase } from '../firebase.js';
import { AccountExistsError, authErrorCode } from './errors.js';

/** Where the email is parked between "send me a link" and opening it. */
const EMAIL_KEY = 'atlas-nook-admin:signin-email';

/**
 * How an account comes in. `email` is the honest label when Firebase will not say which of
 * link or password it is — the lookup is disabled by email-enumeration protection.
 */
export type SignInMethod = 'link' | 'password' | 'google' | 'email';

export interface Session {
  user: User;
  uid: string;
  email: string;
  initials: string;
  /** The catalog-administration claim, and the only thing that opens this app. */
  admin: boolean;
  /** The ways this account signs in — informational, for the account menu. */
  methods: SignInMethod[];
  /** False for a password account that has not opened its verification link yet. */
  emailVerified: boolean;
}

interface AuthValue {
  /** `undefined` while the first token resolves; `null` once we know nobody is signed in. */
  session: Session | null | undefined;
  sendLink: (email: string) => Promise<void>;
  completeLink: (email: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  /** Creates the account, sends the verification email, and signs in without waiting for it. */
  createAccount: (email: string, password: string) => Promise<void>;
  /** Resolves whether or not the address has an account — the page says "sent" either way. */
  resetPassword: (email: string) => Promise<void>;
  /** A popup, `select_account` every time, and never a redirect. */
  signInWithGoogle: () => Promise<void>;
  sendVerification: () => Promise<void>;
  /** A link or Google account's first password. Re-auth-gated by Firebase's own rule. */
  setPassword: (next: string) => Promise<void>;
  /** A password account's new password, proven with the current one. */
  changePassword: (current: string, next: string) => Promise<void>;
  signOutNow: () => Promise<void>;
  /** The email we asked for a link with, if this browser is the one that asked. */
  pendingEmail: string | null;
  /** True when the current URL is a sign-in link waiting to be completed. */
  isLinkArrival: boolean;
  /** The quiet "verify your email" note, until it is dismissed for this visit. */
  verifyNoteDismissed: boolean;
  dismissVerifyNote: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

function initialsFor(user: User): string {
  const source = user.displayName ?? user.email ?? '';
  const words = source.split(/[\s@._-]+/).filter((word) => word !== '');
  const letters = words.slice(0, 2).map((word) => word[0] ?? '');
  return (letters.join('') || '?').toUpperCase();
}

function readStoredEmail(): string | null {
  try {
    return window.localStorage.getItem(EMAIL_KEY);
  } catch {
    return null;
  }
}

function storeEmail(email: string | null): void {
  try {
    if (email === null) window.localStorage.removeItem(EMAIL_KEY);
    else window.localStorage.setItem(EMAIL_KEY, email);
  } catch {
    // Without storage the link still works — it just asks for the email again on arrival.
  }
}

function hasProvider(user: User, providerId: string): boolean {
  return user.providerData.some((info) => info.providerId === providerId);
}

/**
 * The ways an account comes in.
 *
 * `providerData` says *Google* plainly, but files a link account and a password account
 * under the same `password` provider. The lookup tells them apart — unless enumeration
 * protection has switched it off, in which case the honest answer is "email".
 */
async function methodsFor(
  lookup: (email: string) => Promise<string[]>,
  user: User,
): Promise<SignInMethod[]> {
  const methods: SignInMethod[] = [];
  if (hasProvider(user, 'google.com')) methods.push('google');

  if (hasProvider(user, 'password') || user.providerData.length === 0) {
    let known: string[] = [];
    if (user.email !== null) {
      try {
        known = await lookup(user.email);
      } catch {
        known = [];
      }
    }
    if (known.includes('password')) methods.push('password');
    if (known.includes('emailLink')) methods.push('link');
    if (!known.includes('password') && !known.includes('emailLink')) methods.push('email');
  }
  return methods;
}

/** The mail callable, reached lazily — the Functions SDK stays out of the entry chunk. */
async function requestAuthMail(request: {
  kind: 'signInLink' | 'verify' | 'reset';
  email: string;
}): Promise<void> {
  const { requestAuthMailCall } = await import('../data/callables.js');
  await requestAuthMailCall({ ...request, lang: 'en', app: 'admin' });
}

function googleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  // The account picker, every time: a shared tablet must not slide into the last account.
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const { auth } = firebase();
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [pendingEmail, setPendingEmail] = useState<string | null>(() => readStoredEmail());
  const [isLinkArrival, setIsLinkArrival] = useState(false);
  const [verifyNoteDismissed, setVerifyNoteDismissed] = useState(false);
  // Bumped after a password is set or changed, so the menu's method list follows.
  const [methodsVersion, setMethodsVersion] = useState(0);

  useEffect(() => {
    setIsLinkArrival(isSignInWithEmailLink(auth, window.location.href));
  }, [auth]);

  useEffect(
    () =>
      onIdTokenChanged(auth, (user) => {
        if (user === null) {
          setSession(null);
          setVerifyNoteDismissed(false);
          return;
        }
        void Promise.all([
          user.getIdTokenResult(),
          methodsFor((email) => fetchSignInMethodsForEmail(auth, email), user),
        ]).then(([token, methods]) => {
          setSession({
            user,
            uid: user.uid,
            email: user.email ?? '',
            initials: initialsFor(user),
            admin: token.claims['admin'] === true,
            methods,
            emailVerified: user.emailVerified,
          });
        });
      }),
    [auth, methodsVersion],
  );

  const sendLink = useCallback(async (email: string) => {
    // The link comes back to this app's door; the backend builds that URL, never this file.
    await requestAuthMail({ kind: 'signInLink', email });
    storeEmail(email);
    setPendingEmail(email);
  }, []);

  const completeLink = useCallback(
    async (email: string) => {
      await signInWithEmailLink(auth, email, window.location.href);
      storeEmail(null);
      setPendingEmail(null);
      setIsLinkArrival(false);
      // Drop the one-time credential out of the address bar.
      window.history.replaceState(null, '', '/');
    },
    [auth],
  );

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      await signInWithEmailAndPassword(auth, email, password);
    },
    [auth],
  );

  const createAccount = useCallback(
    async (email: string, password: string) => {
      await createUserWithEmailAndPassword(auth, email, password);
      // Sent, not awaited on: the account is in whether or not this email arrives, and a
      // failure here is not a reason to refuse the sign-in that just succeeded.
      await requestAuthMail({ kind: 'verify', email }).catch(() => undefined);
    },
    [auth],
  );

  const resetPassword = useCallback(async (email: string) => {
    // Same reply from the backend whether or not the address has an account — a reset that
    // names which addresses exist is a directory. Only a failed send reaches the page.
    await requestAuthMail({ kind: 'reset', email });
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      await signInWithPopup(auth, googleProvider());
    } catch (error) {
      if (authErrorCode(error) === 'auth/account-exists-with-different-credential') {
        const email = (error as { customData?: { email?: string } }).customData?.email ?? '';
        let methods: string[];
        try {
          methods = email === '' ? [] : await fetchSignInMethodsForEmail(auth, email);
        } catch {
          methods = [];
        }
        throw new AccountExistsError(email, methods);
      }
      throw error;
    }
  }, [auth]);

  const sendVerification = useCallback(async () => {
    const user = auth.currentUser;
    if (user === null || user.email === null) return;
    await requestAuthMail({ kind: 'verify', email: user.email });
  }, [auth]);

  const setPassword = useCallback(
    async (next: string) => {
      const user = auth.currentUser;
      if (user === null || user.email === null) throw new Error('setPassword: not signed in');

      const attempt = async (): Promise<void> => {
        if (hasProvider(user, 'password')) {
          // A link account already carries the `password` provider; its first password is
          // an update, not a link (linking would say `provider-already-linked`).
          await updatePassword(user, next);
          return;
        }
        try {
          await linkWithCredential(user, EmailAuthProvider.credential(user.email ?? '', next));
        } catch (error) {
          // The credential names this account's own address, so "already in use" can only
          // mean *this* account — the Auth emulator answers the link that way (production
          // links). `updatePassword` adds the password provider just the same.
          const code = authErrorCode(error);
          if (code !== 'auth/email-already-in-use' && code !== 'auth/provider-already-linked') {
            throw error;
          }
          await updatePassword(user, next);
        }
      };

      try {
        await attempt();
      } catch (error) {
        // Firebase's own gate: a fresh sign-in first. Google can prove one with a popup; a
        // link account cannot without a new link, so its error reaches the dialog as a line.
        if (
          authErrorCode(error) !== 'auth/requires-recent-login' ||
          !hasProvider(user, 'google.com')
        ) {
          throw error;
        }
        await reauthenticateWithPopup(user, googleProvider());
        await attempt();
      }
      setMethodsVersion((version) => version + 1);
    },
    [auth],
  );

  const changePassword = useCallback(
    async (current: string, next: string) => {
      const user = auth.currentUser;
      if (user === null || user.email === null) throw new Error('changePassword: not signed in');
      // The current password is the recent sign-in Firebase asks for, every time.
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current));
      await updatePassword(user, next);
      setMethodsVersion((version) => version + 1);
    },
    [auth],
  );

  const signOutNow = useCallback(async () => {
    await signOut(auth);
    storeEmail(null);
    setPendingEmail(null);
  }, [auth]);

  const dismissVerifyNote = useCallback(() => setVerifyNoteDismissed(true), []);

  const value = useMemo<AuthValue>(
    () => ({
      session,
      sendLink,
      completeLink,
      signInWithPassword,
      createAccount,
      resetPassword,
      signInWithGoogle,
      sendVerification,
      setPassword,
      changePassword,
      signOutNow,
      pendingEmail,
      isLinkArrival,
      verifyNoteDismissed,
      dismissVerifyNote,
    }),
    [
      session,
      sendLink,
      completeLink,
      signInWithPassword,
      createAccount,
      resetPassword,
      signInWithGoogle,
      sendVerification,
      setPassword,
      changePassword,
      signOutNow,
      pendingEmail,
      isLinkArrival,
      verifyNoteDismissed,
      dismissVerifyNote,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth: no AuthProvider above this component');
  return value;
}

/** The signed-in admin session. Only valid under `RequireAdmin`, which is where it is read. */
export function useSession(): Session {
  const { session } = useAuth();
  if (session === undefined || session === null) {
    throw new Error('useSession: no signed-in session — this component is outside RequireAdmin');
  }
  return session;
}
